import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ColumnDef, FilterFn } from '@tanstack/react-table';

import DataTable from '@/components/ui/DataTable/DataTable';
import { Col } from '@/components/ui/DataTable/ColumnsHelpers';
import Button from '@/components/ui/Button';
import SearchInput from '@/components/ui/SearchInput/SearchInput';
import StatusFilter from '@/components/filters/StatusFilter/StatusFilter';
import FundFilter from '@/components/filters/FundFilter/FundFilter';
import CustomMenu from '@/components/ui/Menu';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import useTableDensity from '@/hooks/useTableDensity';
import { PaymentDeviceStatus } from '@/models/common/PaymentDeviceEnums';
import { PaymentDeviceDtoIn, PaymentDeviceSummaryDtoIn } from '@/models/in/PaymentDeviceDtoIn';
import {
  MANAGEMENT_DEVICES_URL,
  PaymentDeviceCounterKey,
  allowedDeviceActions,
  deviceStatusColor,
  formatDeviceDate,
  summarizeDeviceCounts,
} from '@/helpers/paymentDevices';
import { DeviceStatusChip, LongRentalTag, OverdueTag } from './DeviceStatusChip';
import LongRentalReminder from './LongRentalReminder';
import DeviceBlade from './DeviceBlade';
import HeldRequestsList from './HeldRequestsList';
import { useDeviceDialogs } from './useDeviceDialogs';

const DENSITY_STORAGE_KEY = 'table.density.paymentDevices';

const COUNTER_KEYS: PaymentDeviceCounterKey[] = [
  'total',
  PaymentDeviceStatus.Available,
  PaymentDeviceStatus.Reserved,
  PaymentDeviceStatus.Rented,
  PaymentDeviceStatus.Faulty,
];

const deviceSearch: FilterFn<PaymentDeviceDtoIn> = (row, _columnId, value) => {
  if (!value) return true;
  const query = String(value).toLowerCase();
  const { deviceNumber, notes, currentRental } = row.original;
  return [deviceNumber, notes, currentRental?.fundName, currentRental ? String(currentRental.fundId) : null]
    .some((field) => field?.toLowerCase().includes(query));
};

/**
 * «מלאי»: the counters the office asked for (how many devices, how many faulty, rented and to
 * whom, free), the device table and every action on a device.
 */
interface InventoryTabProps {
  /** Opens a request that holds a free device. */
  onOpenRequest?: (requestId: number) => void;
}

const InventoryTab = ({ onOpenRequest }: InventoryTabProps) => {
  const { t } = useTranslation();
  const [statusFilter, setStatusFilter] = useState<PaymentDeviceStatus | null>(null);
  const [fundFilter, setFundFilter] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLongRentalFilter, setIsLongRentalFilter] = useState(false);
  // The counters filter by status too, so clearing resets them along with the chips, the project and the search.
  const hasActiveFilters = statusFilter != null || fundFilter != null || searchQuery !== '' || isLongRentalFilter;
  const clearFilters = () => {
    setStatusFilter(null);
    setFundFilter(null);
    setSearchQuery('');
    setIsLongRentalFilter(false);
  };
  const [density, setDensity] = useTableDensity(DENSITY_STORAGE_KEY, 'normal');
  const [bladeDeviceId, setBladeDeviceId] = useState<number | null>(null);

  const { data: devices, isLoading } = useGetAuthApi<PaymentDeviceDtoIn[]>(
    `${MANAGEMENT_DEVICES_URL}?includeRetired=${statusFilter === PaymentDeviceStatus.Retired}`,
  );
  const { data: summary } = useGetAuthApi<PaymentDeviceSummaryDtoIn>(`${MANAGEMENT_DEVICES_URL}/summary`);
  const counters = useMemo(() => summarizeDeviceCounts(summary?.counts ?? []), [summary]);

  const { run, openCreate, dialogs } = useDeviceDialogs();

  const rows = useMemo(() => (devices ?? []).filter((d) =>
    (statusFilter == null || d.status === statusFilter)
    && (fundFilter == null || d.currentRental?.fundId === fundFilter)
    && (!isLongRentalFilter || !!d.currentRental?.isLongRental)
  ), [devices, statusFilter, fundFilter, isLongRentalFilter]);
  const longRentals = useMemo(() => (devices ?? []).filter((d) => d.currentRental?.isLongRental), [devices]);
  const longRentalCount = summary?.longRentalCount ?? longRentals.length;

  const columns: ColumnDef<PaymentDeviceDtoIn>[] = [
    {
      id: 'deviceNumber',
      header: Col(t('paymentDevices.columns.deviceNumber')),
      accessorKey: 'deviceNumber',
      enableSorting: true,
      cell: ({ row }) => <span dir="ltr" className="body-sm-semibold">{row.original.deviceNumber}</span>,
      meta: { className: 'min-w-[130px]' },
    },
    {
      id: 'status',
      header: Col(t('paymentDevices.columns.status')),
      accessorKey: 'status',
      enableSorting: true,
      cell: ({ row }) => (
        <span className="inline-flex items-center gap-2 flex-wrap">
          <DeviceStatusChip status={row.original.status} />
          {row.original.currentRental?.isOverdue && <OverdueTag />}
          {row.original.currentRental?.isLongRental && <LongRentalTag />}
        </span>
      ),
      meta: { className: 'min-w-[170px]' },
    },
    {
      id: 'fund',
      header: Col(t('paymentDevices.columns.fund')),
      accessorFn: (row) => row.currentRental?.fundId ?? null,
      enableSorting: true,
      cell: ({ row }) => {
        const rental = row.original.currentRental;
        return rental ? <span>{rental.fundId} | {rental.fundName ?? ''}</span> : <span className="text-(--font-tertiary)">—</span>;
      },
      meta: { className: 'min-w-[200px]' },
    },
    {
      id: 'handedOverDate',
      header: Col(t('paymentDevices.columns.handedOverDate')),
      accessorFn: (row) => row.currentRental?.handedOverDate ?? '',
      enableSorting: true,
      cell: ({ row }) => formatDeviceDate(row.original.currentRental?.handedOverDate) || '—',
      meta: { className: 'min-w-[110px]' },
    },
    {
      id: 'expectedReturnDate',
      header: Col(t('paymentDevices.columns.expectedReturnDate')),
      accessorFn: (row) => row.currentRental?.expectedReturnDate ?? '',
      enableSorting: true,
      cell: ({ row }) => formatDeviceDate(row.original.currentRental?.expectedReturnDate) || '—',
      meta: { className: 'min-w-[110px]' },
    },
    {
      id: 'notes',
      header: Col(t('paymentDevices.columns.notes')),
      accessorKey: 'notes',
      enableSorting: false,
      cell: ({ row }) => <span className="line-clamp-2">{row.original.notes || '—'}</span>,
      meta: { className: 'min-w-[180px]' },
    },
    {
      id: 'actions',
      header: Col(''),
      enableSorting: false,
      cell: ({ row }) => {
        const device = row.original;
        const items = allowedDeviceActions(device).map((action) => ({
          text: t(`paymentDevices.actions.${action}`),
          onClick: () => run(action, device),
          danger: action === 'delete' || action === 'retire',
        }));
        return <CustomMenu items={items} fillCell />;
      },
      meta: { className: 'min-w-[60px] max-w-[60px]', lockLayout: true },
    },
  ];

  const counterTitle = (key: PaymentDeviceCounterKey) =>
    (key === 'total' ? t('paymentDevices.counters.total') : t(`paymentDevices.status.${key}`));

  return (
    <div className="flex flex-col gap-4">
      {longRentalCount > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-orange-200 bg-orange-50 px-4 py-3 text-orange-700">
          <span className="body-sm-semibold">{t('paymentDevices.longRental.banner', { count: longRentalCount })}</span>
          <Button
            variant="outline"
            onClick={() => {
              setStatusFilter(null);
              setIsLongRentalFilter(true);
            }}
          >
            {t('paymentDevices.longRental.show')}
          </Button>
        </div>
      )}

      <div className="flex flex-wrap gap-3">
        {COUNTER_KEYS.map((key) => {
          const counter = counters[key];
          const isSelected = key === 'total' ? statusFilter == null : statusFilter === key;
          return (
            <button
              key={key}
              type="button"
              onClick={() => setStatusFilter(key === 'total' ? null : (key as PaymentDeviceStatus))}
              className={`flex flex-col items-start gap-1 rounded-xl border px-4 py-3 min-w-[150px] text-start bg-white ${isSelected ? 'border-(--border-primary) shadow-sm' : 'border-(--border-main)'}`}
            >
              <span className="inline-flex items-center gap-2 body-sm-semibold text-(--font-secondary)">
                {key !== 'total' && (
                  <span className="w-[10px] h-[10px] rounded-full inline-block" style={{ backgroundColor: deviceStatusColor(key as PaymentDeviceStatus) }} />
                )}
                {counterTitle(key)}
              </span>
              <span className="heading-h3-semibold">{counter.total}</span>
              {key === PaymentDeviceStatus.Available && (summary?.heldCount ?? 0) > 0 && (
                <span className="body-sm-regular text-(--font-tertiary)">
                  {t('paymentDevices.counters.held', { count: summary?.heldCount ?? 0 })}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {(summary?.heldRequests?.length ?? 0) > 0 && (
        // Every free device says «פנוי» in the list below, held or not: say which requests hold them.
        <div className="mb-4 flex flex-col gap-1 rounded-xl border border-(--border-main) bg-white px-4 py-3">
          <span className="body-sm-semibold">
            {t('paymentDevices.held.title', { free: counters[PaymentDeviceStatus.Available].total, count: summary?.heldCount ?? 0 })}
          </span>
          <HeldRequestsList requests={summary?.heldRequests ?? []} onOpen={onOpenRequest} />
        </div>
      )}

      <DataTable
        title={t('paymentDevices.title')}
        data={rows}
        isLoading={isLoading}
        columns={columns}
        filters={
          <div className="flex flex-wrap items-center gap-3">
            <Button variant="primary" onClick={openCreate}>
              {t('paymentDevices.addDevice')}
            </Button>
            <SearchInput value={searchQuery} onChange={setSearchQuery} placeholder="paymentDevices.search" />
            <StatusFilter
              title={t('paymentDevices.status.Retired')}
              iconColor={deviceStatusColor(PaymentDeviceStatus.Retired)}
              isSelected={statusFilter === PaymentDeviceStatus.Retired}
              onClick={() => setStatusFilter(statusFilter === PaymentDeviceStatus.Retired ? null : PaymentDeviceStatus.Retired)}
            />
            <FundFilter selectedFundId={fundFilter} onFundChange={setFundFilter} />
            {hasActiveFilters && (
              <Button variant="outline" onClick={clearFilters}>{t('clearFilters')}</Button>
            )}
          </div>
        }
        emptyStateContent={{ title: t('paymentDevices.emptyInventory.title'), description: t('paymentDevices.emptyInventory.description') }}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        customGlobalSearch={deviceSearch}
        density={density}
        onDensityChange={setDensity}
        columnLayoutKey="paymentDevices"
        handleRowClick={(device) => setBladeDeviceId(device.id)}
        getRowId={(device) => String(device.id)}
      />

      {bladeDeviceId != null && (
        <DeviceBlade deviceId={bladeDeviceId} onClose={() => setBladeDeviceId(null)} onAction={run} />
      )}

      <LongRentalReminder devices={longRentals} />

      {dialogs}
    </div>
  );
};

export default InventoryTab;
